import { Skeleton } from '@mui/material';
import React, {
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import NewsCard from '../../presentation/features/news/NewsCard/NewsCard';
import NewsPreview from '../../presentation/features/news/NewsPreview/NewsPreview';
import PageHeader from '../../presentation/layout/PageHeader/PageHeader';
import { fetchRSSNews } from '../../services/apiService';
import { useAppInsightsComponentLifetimeTracking } from '../../services/appInsights/useAppInsightsComponentTracking';
import { useI18n } from '../../hooks/useI18n';
import { SelectedNewsContext } from '../../context/SelectedNewsContext';
import {
  useContextStore,
  useSurveyRedCertStore,
  useDocumentsStore,
} from '../../stores/storeRoot';
import { MainContainer } from '../../styles/styles';
import {
  CardsContainer,
  ContentWrapper,
  DetailViewWrapper,
  FadeContainer,
  LeftPlaceholder,
  RightPlaceholder,
  StyledCardWrapper,
  WelcomePlaceholder,
} from './styles';
import { TranslationKeys } from '../../i18n/locales/defaultResources';
import { NewsItem } from './types';
import { useTranslationContext } from '../../context/TranslationContext';
import ApplicationLoader from '../../presentation/components/ApplicationLoader/ApplicationLoader';
import {
  ContentComponent,
  DateTypography,
  ImageComponent,
  StyledBox,
  StyledCard,
  SummaryAndLinkContainer,
  TitleTypography,
} from '../../presentation/features/news/NewsCard/styles';
import ArgeNordBanner from '../../presentation/features/arge_nord_banner/ArgeNordBanner/ArgeNordBanner';
import BannerPl from '../../presentation/features/bannerPl/bannerPl';
import SecurityTrim from '../../services/auth/SecurityTrim';
import BannerApc from '../../presentation/features/bannerAPC/bannerApc';
import {
  CropConnectRoleDefinitions,
  SurveyQuestionDto,
  DocumentFolder,
  DocumentSnapshotDto,
} from '../../services/cropConnectApiClient';
import {
  getLanguageCodeFromLocale,
  mapLocaleAccordingToNzRules,
} from '../../helpers/localeFormatter';
import GeneralDocumentsComponent from '../../presentation/features/general_documents/GeneralDocumentsComponent/GeneralDocumentsComponent';
import { useMyTaskStore } from '../../stores/myTaskStore';
import SurveyFormModal from '../../presentation/features/common_modals/SurveyFormModal/SurveyFormModal';
import ActionButton from '../../presentation/components/ActionButton/ActionButton';
import { ConfirmActionModal } from '../../presentation/features/survey_red_cert/ConfirmActionModal/ConfirmActionModal';
import MyTasksWidget from '../../presentation/features/my_tasks/MyTasksWidget/MyTasksWidget';
import DocumentsWidget from '../../presentation/features/documents/DocumentsWidget/DocumentsWidget';
import {
  individualDocumentsAllPath,
  individualDocumentsNewslettersPath,
} from '../../routes';
import UploadBannerComponent from '../../presentation/features/upload_banner/UploadBannerComponent/UploadBannerComponent';

const mapToDocument = (doc: DocumentSnapshotDto) => ({
  name: doc.name || '',
  url: '',
  id: doc.id || '',
});

const Home: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const surveyId = searchParams.get('surveyId');
  const { tasks, fetchTasks } = useMyTaskStore();
  const { getSurveyFormForSurvey } = useSurveyRedCertStore();

  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);

  const [newsData, setNewsData] = useState<NewsItem[]>([]);
  const [imageLoaded, setImageLoaded] = useState<boolean[]>([]);
  const { user, activeBpNumberCountry, contextUpdating, activeBpNumber } =
    useContextStore();
  const [isUserLoaded, setIsUserLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isMainView, setIsMainView] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [newsCardsRendered, setNewsCardsRendered] = useState(false);
  const [isSurveyModalOpen, setIsSurveyModalOpen] = useState(false);
  const [surveyName, setSurveyName] = useState<string>('');
  const [currentSurveyAccessId, setCurrentSurveyAccessId] = useState<
    string | null
  >(null);

  const loaderRef = useRef(false);
  const { t } = useI18n();
  const { isTranslationMode } = useTranslationContext();
  const { selectedArticle, setSelectedArticle } =
    useContext(SelectedNewsContext);
  const location = useLocation();
  const { getDocumentsPage, getAllInboxDocumentsPage } = useDocumentsStore();
  const [inboxUnreadCount, setInboxUnreadCount] = useState(0);
  const [allIndividualDocuments, setAllIndividualDocuments] = useState<
    DocumentSnapshotDto[]
  >([]);
  const [newslettersIndividualDocuments, setNewslettersIndividualDocuments] =
    useState<DocumentSnapshotDto[]>([]);

  const handleImageLoad = (index: number) => {
    setImageLoaded((prev) => {
      const newImageLoaded = [...prev];
      newImageLoaded[index] = true;
      return newImageLoaded;
    });
  };

  const loadNewsData = () => {
    const mappedLocale = mapLocaleAccordingToNzRules(
      activeBpNumberCountry || ''
    );
    const newsLanguageToLoad = getLanguageCodeFromLocale(mappedLocale);

    fetchRSSNews(newsLanguageToLoad)
      .then((data) => {
        setNewsData(data.rss.channel.item);
        setImageLoaded(new Array(data.rss.channel.item.length).fill(false));
        setIsLoading(false);
      })
      .catch((error) => {
        console.error('Failed to fetch RSS news:', error);
        setIsLoading(false);
      });
  };

  const [surveyFormData, setSurveyFormData] = useState<{
    questions: SurveyQuestionDto[];
    additionalData: never;
    filledBy: string | null;
    answers: Array<{ key: string; value: string }>;
    filledOut: boolean;
    surveyAdditionalData?: {
      description?: string;
      hint?: {
        text: string;
        enabled: boolean;
      };
    };
  } | null>(null);

  const handleOpenSurveyModal = async (surveyTaskContext: string) => {
    try {
      if (!surveyTaskContext) {
        return;
      }

      const getSurveyFormResponse =
        await getSurveyFormForSurvey(surveyTaskContext);

      if (getSurveyFormResponse?.data) {
        setSurveyName(getSurveyFormResponse.data.surveyName);
        setSurveyFormData({
          questions: getSurveyFormResponse.data.questions,
          additionalData: {
            invekosno: getSurveyFormResponse.data.additionalData.invekosno,
            lastSubmissionTime:
              getSurveyFormResponse.data.additionalData.lastSubmissionTime,
          },
          filledBy: getSurveyFormResponse.data.filledBy,
          answers: getSurveyFormResponse.data.answers,
          filledOut: getSurveyFormResponse.data.filledOut,
          surveyAdditionalData: getSurveyFormResponse.data.surveyAdditionalData,
        });
      }

      setCurrentSurveyAccessId(surveyTaskContext);
      setIsSurveyModalOpen(true);
      if (typeof window != 'undefined' && window.document) {
        document.body.style.overflow = 'hidden';
      }
    } catch (error) {
      console.error('Error in handleOpenSurveyModal:', error);
    }
  };

  const surveyTask = tasks.find(
    (task) => task.type === 'FILL_RED_CERT_SURVEY' && !task.isCompleted
  );

  useEffect(() => {
    if (surveyId) {
      handleOpenSurveyModal(surveyId);
    }
  }, [surveyId]);

  useEffect(() => {
    fetchTasks();
  }, []);

  useEffect(() => {
    loadNewsData();
  }, [activeBpNumberCountry]);

  useEffect(() => {
    if (user) {
      setIsUserLoaded(true);
    }
  }, [user]);

  useEffect(() => {
    useDocumentsStore.getState().initializeDocumentsStore();
    if (location.state?.reset) {
      setIsMainView(true);
      loadNewsData();
    }
  }, [location]);

  const handleTransition = (article: NewsItem | null): void => {
    setIsTransitioning(true);
    setTimeout(() => {
      if (article) {
        setSelectedArticle(article);
        setIsMainView(false);
      } else {
        setSelectedArticle(null);
        setIsMainView(true);
        loadNewsData();
      }
      setIsTransitioning(false);
    }, 300);
    // scrolls the article to the beginning
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    if (!isMainView) {
      handleTransition(null);
    }
  }, [activeBpNumberCountry]);

  useEffect(() => {
    setNewslettersIndividualDocuments([]);
    setAllIndividualDocuments([]);
    getDocumentsPage(DocumentFolder.MyNewsletter, 3, 0, 'creationDate').then(
      (d) => {
        setNewslettersIndividualDocuments(d?.documents || []);
      }
    );
    getAllInboxDocumentsPage(activeBpNumberCountry, 3).then((d) => {
      setAllIndividualDocuments(d?.documents || []);
      setInboxUnreadCount(d?.unreadItemsCount || 0);
    });
  }, [
    activeBpNumber,
    getDocumentsPage,
    getAllInboxDocumentsPage,
    activeBpNumberCountry,
  ]);

  const renderRelatedArticles = (): NewsItem[] => {
    if (!selectedArticle) return [];
    const filteredArticles = newsData.filter(
      (article) => article.guid !== selectedArticle.guid
    );
    return filteredArticles.slice(0, 4);
  };

  const { globalAssignment, isAccessOnBehalf } = useContextStore();

  useAppInsightsComponentLifetimeTracking(
    'NewsPreview',
    {
      newsTitle: selectedArticle?.title,
      newsId: selectedArticle?.guid,
    },
    [selectedArticle],
    () => !!selectedArticle
  );

  const handleReadMore = (article: NewsItem): void => {
    handleTransition(article);
  };

  useLayoutEffect(() => {
    if (!isLoading && newsCardsRendered && !contextUpdating) {
      loaderRef.current = false;
    }
  }, [isLoading, newsCardsRendered, contextUpdating]);

  const handleArgeNordBannerClick = () => {
    window.open(process.env.AGRE_NORD_PARTNER, '_blank');
  };

  const handleBannerPlClick = () => {
    window.open(process.env.BANNER_PL_PARTNER, '_blank');
  };

  const handleBannerApcClick = () => {
    window.open(process.env.APC_BANNER_PARTNER, '_blank');
  };

  return (
    <>
      {loaderRef.current ? (
        <ApplicationLoader withBlur={true} />
      ) : (
        <MainContainer>
          {!isAccessOnBehalf ||
            (isAccessOnBehalf &&
              globalAssignment?.roles?.some(
                (r) => r === CropConnectRoleDefinitions.RedCertAdmin
              ))}
          <>
            {surveyTask && surveyTask.context && (
              <div
                style={{
                  display: 'flex',
                  gap: '12px',
                }}
              >
                <h5>{t(TranslationKeys.my_tasks_survey_available)}</h5>
                <ActionButton
                  variant="link-button"
                  buttonText={t(TranslationKeys.my_tasks_fill_survey)}
                  onClickAction={() =>
                    handleOpenSurveyModal(surveyTask.context)
                  }
                />
              </div>
            )}
          </>
          <FadeContainer
            className={
              isTransitioning ? 'fade-exit-active' : 'fade-enter-active'
            }
          >
            {isMainView ? (
              <>
                <ContentWrapper>
                  <LeftPlaceholder>
                    <PageHeader
                      title={
                        isTranslationMode ? (
                          t(TranslationKeys.homeNews_welcome_text)
                        ) : isUserLoaded ? (
                          `${t(TranslationKeys.homeNews_welcome_text)}, ${
                            user?.displayName || 'User'
                          }`
                        ) : (
                          <WelcomePlaceholder />
                        )
                      }
                    />
                    <CardsContainer>
                      {isLoading
                        ? Array.from({ length: 24 }).map((_, index) => (
                            <div
                              key={`skeleton-${index}`}
                              className="fade-enter fade-enter-active"
                            >
                              <SkeletonCard />
                            </div>
                          ))
                        : newsData.map((newsItem, index) => (
                            <StyledCardWrapper
                              key={newsItem.guid}
                              className="fade-enter fade-enter-active"
                            >
                              <NewsCard
                                link={newsItem.link}
                                title={newsItem.title}
                                description={
                                  newsItem.description?.['$cdata_section']
                                }
                                countryIsoCode={activeBpNumberCountry}
                                pubDate={newsItem.pubDate}
                                content={
                                  newsItem['content$encoded']?.[
                                    '$cdata_section'
                                  ]
                                }
                                imageUrl={
                                  newsItem.enclosure
                                    ? newsItem.enclosure['@url']
                                    : undefined
                                }
                                guid={newsItem.guid}
                                onReadMore={() => handleReadMore(newsItem)}
                                onImageLoad={() => handleImageLoad(index)}
                                isImageLoaded={imageLoaded[index]}
                                onSkeletonComplete={() =>
                                  setNewsCardsRendered(true)
                                }
                              />
                            </StyledCardWrapper>
                          ))}
                    </CardsContainer>
                  </LeftPlaceholder>
                  <RightPlaceholder>
                    <MyTasksWidget />
                    <UploadBannerComponent parent="home" />
                    <GeneralDocumentsComponent parent="home" />
                    <SecurityTrim
                      requiredBusinessPartnerRoles={[
                        CropConnectRoleDefinitions.Grower,
                      ]}
                    >
                      <DocumentsWidget
                        documents={allIndividualDocuments.map(mapToDocument)}
                        onViewAllClick={() =>
                          navigate(individualDocumentsAllPath)
                        }
                        title={
                          t(
                            TranslationKeys.individual_documents_widget_inbox_title
                          ) as string
                        }
                        unreadCount={inboxUnreadCount}
                      />
                    </SecurityTrim>
                    <SecurityTrim
                      requiredBusinessPartnerRoles={[
                        CropConnectRoleDefinitions.Grower_DE,
                        CropConnectRoleDefinitions.Grower_Organic_DE,
                        CropConnectRoleDefinitions.Grower_FI,
                      ]}
                    >
                      <DocumentsWidget
                        documents={newslettersIndividualDocuments.map(
                          mapToDocument
                        )}
                        onViewAllClick={() =>
                          navigate(individualDocumentsNewslettersPath)
                        }
                        title={
                          t(
                            TranslationKeys.individual_documents_widget_newsletters_title
                          ) as string
                        }
                      />
                    </SecurityTrim>
                    <SecurityTrim
                      requiredBusinessPartnerRoles={[
                        CropConnectRoleDefinitions.Grower_DE,
                      ]}
                    >
                      <ArgeNordBanner onClick={handleArgeNordBannerClick} />
                    </SecurityTrim>
                    <SecurityTrim
                      requiredBusinessPartnerRoles={[
                        CropConnectRoleDefinitions.Grower_PL,
                      ]}
                    >
                      <BannerPl onClick={handleBannerPlClick} />
                    </SecurityTrim>
                    <SecurityTrim
                      requiredBusinessPartnerRoles={[
                        CropConnectRoleDefinitions.Grower_DE,
                      ]}
                    >
                      <BannerApc onClick={handleBannerApcClick} />
                    </SecurityTrim>
                  </RightPlaceholder>
                </ContentWrapper>
              </>
            ) : (
              <DetailViewWrapper>
                <LeftPlaceholder>
                  <PageHeader title={selectedArticle?.title || 'News Detail'} />
                </LeftPlaceholder>
                {selectedArticle && (
                  <div className="fade-enter fade-enter-active">
                    <NewsPreview
                      selectedArticle={selectedArticle}
                      relatedArticles={renderRelatedArticles()}
                      onReadMore={handleReadMore}
                      onBack={() => handleTransition(null)}
                      countryIsoCode={activeBpNumberCountry}
                      transitionClass="fade"
                    />
                  </div>
                )}
              </DetailViewWrapper>
            )}
          </FadeContainer>
        </MainContainer>
      )}

      <ConfirmActionModal
        isOpen={isSuccessModalOpen}
        onClose={() => {
          setIsSuccessModalOpen(false);
        }}
        onConfirm={() => {
          setIsSuccessModalOpen(false);
          fetchTasks();
        }}
        title={t(TranslationKeys.survey_form_success_message_title) as string}
        content={t(TranslationKeys.survey_form_modal_success_message) as string}
        confirmButtonText={
          t(TranslationKeys.survey_form_success_confirm_button) as string
        }
        isSuccessMessageToConfirm={true}
      />

      <SurveyFormModal
        open={isSurveyModalOpen}
        onClose={() => {
          setIsSurveyModalOpen(false);
          navigate('/');
          document.body.style.overflow = 'overlay';
        }}
        surveyName={surveyName}
        questions={surveyFormData?.questions || []}
        surveyId={currentSurveyAccessId || ''}
        filledBy={surveyFormData?.filledBy}
        lastSubmissionTime={
          surveyFormData?.additionalData?.lastSubmissionTime ||
          new Date().toISOString()
        }
        additionalProperties={surveyFormData?.surveyAdditionalData}
        invekosNumber={surveyFormData?.additionalData?.invekosno || ''}
        onSubmitSuccess={() => {
          setIsSuccessModalOpen(true);
          setIsSurveyModalOpen(false);
          navigate('/');
          document.body.style.overflow = 'overlay';
        }}
        previousAnswers={surveyFormData?.answers}
        isFilledOut={surveyFormData?.filledOut}
      />
    </>
  );
};

const SkeletonCard: React.FC = () => (
  <StyledCard style={{ margin: '0', textAlign: 'left', width: '900px' }}>
    <StyledBox>
      <ImageComponent isImageLoaded={false}>
        <Skeleton
          variant="rectangular"
          width={256}
          height={156}
          animation="wave"
        />
      </ImageComponent>
      <ContentComponent>
        <DateTypography>
          <Skeleton width="80%" animation="wave" />
        </DateTypography>
        <TitleTypography>
          <Skeleton width="60%" animation="wave" />
        </TitleTypography>
        <SummaryAndLinkContainer>
          <Skeleton width="90%" animation="wave" />
          <Skeleton width="30%" animation="wave" />
        </SummaryAndLinkContainer>
      </ContentComponent>
    </StyledBox>
  </StyledCard>
);

export default Home;
